<template>
  <!-- Teleported to body so the lock covers any open bottom sheet (#sheet-portal, z 1000). -->
  <Teleport to="body">
  <div class="app-lock" role="dialog" aria-modal="true" :aria-label="t('applock.title')">
    <PinGateHead :title="t('login.lock.title')" :sub="error || t('login.pin.enter')" :error="!!error" />

    <div class="app-lock__dots" :class="{ 'app-lock__dots--error': shake }">
      <span v-for="i in 6" :key="i" class="app-lock__dot" :class="{ 'app-lock__dot--on': i <= pin.length }" />
    </div>

    <div class="app-lock__spacer" />

    <PinKeypad :bio="faceIdAvailable" :disabled="busy" @digit="onDigit" @backspace="onBackspace" @biometric="runFaceId" />

    <button class="app-lock__signout" type="button" @click="signOut">{{ t('applock.signout') }}</button>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppLock } from '@/composables/useAppLock';
import { useAuthStore } from '@/stores/auth';
import { loginWithPasskey, passkeyEnrolledOnDevice, forgetPasskeyDevice } from '@/api/webauthn';
import { currentProduct } from '@/product';
import PinKeypad from '@/components/ui/controls/PinKeypad.vue';
import PinGateHead from '@/components/ui/lock/PinGateHead.vue';

const { t } = useI18n();
const appLock = useAppLock();
const auth = useAuthStore();

const pin = ref('');
const busy = ref(false);
const error = ref('');
const shake = ref(false);

// Face ID is offered (identical to the login PIN screen) when a passkey is enrolled on this device
// and we know the last-used identity to re-prove.
const lastEmail = readLastEmail();
const faceIdAvailable = computed(() => lastEmail.trim() !== '' && passkeyEnrolledOnDevice());

function readLastEmail(): string {
  try { return localStorage.getItem('vitrine-last-email') ?? ''; } catch { return ''; }
}

async function runFaceId() {
  if (busy.value || !faceIdAvailable.value) return;
  error.value = '';
  busy.value = true;
  try {
    const r = await loginWithPasskey(lastEmail.trim(), currentProduct() ?? 'vitrine');
    // Server rejected the assertion → stale device flag; forget it so Face ID stops being offered.
    if (r.error) { forgetPasskeyDevice(); error.value = t('login.bio.failed'); return; }
    auth.setTokens(r);
    appLock.markUnlocked();
  } catch (e: unknown) {
    // Cancel (NotAllowedError/AbortError) → stay on the PIN keypad silently, keep Face ID. A real
    // failure means the device flag is stale → forget it so we stop offering a dead-end Face ID.
    const name = (e as { name?: string })?.name;
    if (name !== 'NotAllowedError' && name !== 'AbortError') { forgetPasskeyDevice(); error.value = t('login.bio.failed'); }
  } finally {
    busy.value = false;
  }
}

function onDigit(d: string) {
  if (busy.value || pin.value.length >= 6) return;
  error.value = '';
  pin.value += d;
  if (pin.value.length === 6) void submit();
}
function onBackspace() {
  if (busy.value) return;
  error.value = '';
  pin.value = pin.value.slice(0, -1);
}

async function submit() {
  if (busy.value) return;
  busy.value = true;
  try {
    const r = await appLock.unlock(pin.value);
    if (!r.ok) {
      // NOT_SET = this device is no longer recognized (cookie lost/revoked) → can't unlock, re-login.
      if (r.status === 'NOT_SET') { await signOut(); return; }
      error.value = r.status === 'LOCKED_OUT' ? t('applock.locked') : t('applock.wrong');
      shake.value = true;
      pin.value = '';
      setTimeout(() => { shake.value = false; }, 450);
    }
  } catch {
    error.value = t('applock.wrong');
    shake.value = true;
    pin.value = '';
    setTimeout(() => { shake.value = false; }, 450);
  } finally {
    busy.value = false;
  }
}

async function signOut() {
  await auth.logout();           // clear session first so the screen behind isn't briefly exposed
  appLock.reset();               // then drop the overlay
  const { router } = await import('@/router');
  await nextTick();
  await router.push({ name: 'login' }).catch(() => { /* already there */ });
}
</script>

<style scoped lang="scss">
.app-lock {
  position: fixed; inset: 0; z-index: 3000;
  display: flex; flex-direction: column; align-items: center;
  text-align: center;
  padding: max(40px, var(--app-safe-top)) 24px 30px;
  background: var(--cream);
  overflow: hidden;
}
/* The prototype's dots: 10 px rings (#b8a99a) 18 px apart, bronze when filled. */
.app-lock__dots {
  position: relative; z-index: 1; display: flex; gap: 18px; justify-content: center; margin-top: 30px;
  &--error { animation: app-lock-shake 0.4s; }
}
.app-lock__dot {
  width: 10px; height: 10px; border-radius: 50%; box-sizing: border-box;
  border: 1px solid var(--taupe);
  transition: background 0.15s, transform 0.15s, border-color 0.15s;
}
.app-lock__dot--on { background: var(--bronze); border-color: var(--bronze); transform: scale(1.1); }
.app-lock__dots--error .app-lock__dot { background: var(--pin-error); border-color: var(--pin-error); transform: scale(1); }
.app-lock > :deep(:not(.pin-gate__orbit)) { position: relative; z-index: 1; }

.app-lock__spacer { flex: 1; min-height: 20px; }

.app-lock__signout {
  margin: 22px 0 8px; background: none; border: none; cursor: pointer;
  color: var(--text-3); font-size: 12px; font-family: inherit;
  &:hover { color: var(--gold); }
}

@keyframes app-lock-shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-9px); }
  40% { transform: translateX(8px); }
  60% { transform: translateX(-6px); }
  80% { transform: translateX(4px); }
}
</style>
